import { SecureRoute } from '@okta/okta-react';
import React, { FC, useEffect } from 'react';
import { Redirect, Switch, useParams } from 'react-router-dom';
import { ProjectId } from 'api/models';
import { useProjectContext } from 'components/organisms/customer/layout';
import { ProjectConversation } from 'components/organisms/projects/project-conversation';
import { ProjectTasksPage } from 'components/pages';
import { FormResponseLandingPage } from 'components/pages/project-details/form-responses';
import { RoutePaths } from 'constant';
import { CurrentProjectContext } from 'contexts';
import { useProjectDetail } from 'data/projects/hooks';
import { useProjectTasks } from 'data/tasks/hooks';
import { useUser } from 'hooks/use-user';
import { useProjectWebsocket } from 'hooks/websockets';
import { PageLoading } from 'routes/projects/detail';
import { ProjectConversationPageResponsive } from 'routes/projects/detail/conversation-responsive';
import { ProjectFilesPage } from 'routes/projects/detail/files';
import { ProjectDetailError } from 'routes/projects/error';
import { getProjectPlanUrl } from 'utils/urls';
import { NotFound } from '../../404';
import { SpacesDetailPage } from '../../projects/detail/spaces/space-detail';
import { ProjectSettings as ProjectSettingsPage } from '../../projects/settings';
import { ProjectOverviewPage } from './overview';
import { ProjectPlanPage } from './plan';
import { ProjectStatusUpdatesPage } from './status-updates';

interface Params {
  projectId: string;
}

export const ProjectDependentRoutes: FC = () => {
  const { projectId: _projectId } = useParams<Params>();
  const projectId = ProjectId(parseInt(_projectId, 10));
  const { project, setProject } = useProjectContext();
  const {
    data: projectDetail,
    isLoading: isProjectDetailLoading,
    error,
  } = useProjectDetail(projectId);

  useProjectTasks(
    projectId,
  ); /* Always fetch tasks when we use project detail */

  useProjectWebsocket(projectId);

  const { projects: userAccessibleProjects } = useUser();

  useEffect(() => {
    const current = userAccessibleProjects.find(
      (p) => p.projectId === projectId[1],
    );

    if (current != null && current.projectId !== project?.projectId) {
      setProject(current);
    }
  }, [userAccessibleProjects, projectId, project, setProject]);

  return isProjectDetailLoading ? (
    <PageLoading pinned />
  ) : projectDetail ? (
    <CurrentProjectContext.Provider value={projectDetail}>
      <Switch>
        <SecureRoute path={RoutePaths.projectPlan}>
          <ProjectPlanPage project={projectDetail} />
        </SecureRoute>
        <SecureRoute path={RoutePaths.projectConversationDetails} exact>
          <ProjectConversation projectId={projectId} />
        </SecureRoute>
        <SecureRoute path={RoutePaths.projectConversation} exact>
          <ProjectConversation projectId={projectId} />
        </SecureRoute>
        <SecureRoute
          path={RoutePaths.projectConversationDetailsResponsive}
          exact
        >
          <ProjectConversationPageResponsive />
        </SecureRoute>
        <SecureRoute path={RoutePaths.projectTasks}>
          <ProjectTasksPage project={projectDetail} />
        </SecureRoute>
        <SecureRoute path={RoutePaths.projectSettings}>
          <ProjectSettingsPage />
        </SecureRoute>
        <SecureRoute path={RoutePaths.projectSpaceDetail}>
          <SpacesDetailPage />
        </SecureRoute>
        <SecureRoute path={RoutePaths.projectUpdates} exact>
          <ProjectStatusUpdatesPage />
        </SecureRoute>
        <SecureRoute path={RoutePaths.projectUpdateDetail} exact>
          <ProjectStatusUpdatesPage />
        </SecureRoute>
        <SecureRoute path={RoutePaths.projectOverview} exact>
          <ProjectOverviewPage project={projectDetail} />
        </SecureRoute>
        <SecureRoute path={RoutePaths.projectDetail} exact>
          <Redirect to={getProjectPlanUrl(projectId)} />
        </SecureRoute>
        <SecureRoute path={RoutePaths.projectFiles} exact>
          <ProjectFilesPage project={projectDetail} />
        </SecureRoute>
        <SecureRoute
          path={[
            RoutePaths.projectFormResponses,
            RoutePaths.projectFormResponseByInstance,
          ]}
          exact
        >
          <FormResponseLandingPage project={projectDetail} />
        </SecureRoute>
      </Switch>
    </CurrentProjectContext.Provider>
  ) : error ? (
    <ProjectDetailError error={error} />
  ) : (
    <NotFound />
  );
};
